/**
 * Плотная таблица. Спецификация — docs/04 §6.4.
 *
 * Виртуализация не опция: списки задач исчисляются сотнями тысяч строк, а в DOM
 * при любом их размере должно оставаться 30–50 строк (CLAUDE.md, производительность).
 */

import * as React from "react";
import type { ColumnSizingState } from "@tanstack/react-table";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type Row,
} from "@tanstack/react-table";
import { useVirtualizer } from "@tanstack/react-virtual";

import { cn } from "@/shared/lib/cn";
import { SkeletonRows } from "@/shared/ui/Feedback";

export interface DataGridProps<T> {
  data: readonly T[];
  columns: ColumnDef<T>[];
  /** Высота области прокрутки: виртуализации нужен фиксированный контейнер. */
  height?: number;
  loading?: boolean;
  emptyState?: React.ReactNode;
  onRowClick?: (row: Row<T>) => void;
  /** Подсветка выбранной строки: клавиатурная навигация по списку. */
  selectedRowId?: string;
  className?: string;
  ariaLabel: string;
  /** Ширины колонок: хранятся у пользователя, поэтому состоянием управляет экран. */
  columnSizing?: ColumnSizingState;
  onColumnSizingChange?: (sizing: ColumnSizingState) => void;
  /** Прокрутка до конца: бесконечный список догружает следующую страницу. */
  onReachEnd?: () => void;
  /**
   * Цвет засечки слева в строке — например, по статусу задачи.
   *
   * Рисуется внутренней тенью, а не границей: тень не занимает места в потоке,
   * поэтому ячейки остаются на своих местах и не расходятся с шапкой. Ширину
   * задаёт оформление токеном `--pt-row-accent-w`; в «Тихой бумаге» она нулевая,
   * и засечки не видно.
   */
  rowAccent?: (row: Row<T>) => string | undefined;
}

export function DataGrid<T>({
  data,
  columns,
  height = 480,
  loading = false,
  emptyState,
  onRowClick,
  selectedRowId,
  className,
  ariaLabel,
  columnSizing,
  onColumnSizingChange,
  onReachEnd,
  rowAccent,
}: DataGridProps<T>): React.ReactElement {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const table = useReactTable({
    data: data as T[],
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
    enableColumnResizing: onColumnSizingChange !== undefined,
    state: columnSizing ? { columnSizing } : {},
    onColumnSizingChange: onColumnSizingChange
      ? (updater) => {
          const next =
            typeof updater === "function" ? updater(columnSizing ?? {}) : updater;
          onColumnSizingChange(next);
        }
      : undefined,
  });

  const rows = table.getRowModel().rows;

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    // Высота строки задаётся плотностью, поэтому читается из вычисленных стилей
    estimateSize: () => {
      const value = getComputedStyle(document.documentElement).getPropertyValue("--pt-row-h");
      return Number.parseInt(value, 10) || 32;
    },
    overscan: 10,
  });

  if (loading) {
    return (
      <div className={cn("p-[var(--pt-space-12)]", className)} style={{ height }}>
        <SkeletonRows count={12} />
      </div>
    );
  }

  if (rows.length === 0 && emptyState) {
    return <div className={className}>{emptyState}</div>;
  }

  return (
    <div
      ref={containerRef}
      className={cn("overflow-auto", className)}
      style={{ height }}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
      onScroll={(event) => {
        if (!onReachEnd) {
          return;
        }
        const element = event.currentTarget;
        // Порог в две высоты экрана: страница успевает подгрузиться до того,
        // как человек упрётся в конец списка
        if (element.scrollHeight - element.scrollTop < element.clientHeight * 3) {
          onReachEnd();
        }
      }}
    >
      <table className="w-full table-fixed border-collapse text-[var(--pt-font-ui)]">
        <thead className="sticky top-0 z-[var(--pt-z-sticky)] bg-[var(--pt-bg-surface)]">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="flex w-full items-center">
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  // Та же раскладка, что у строк: браузер иначе пересчитывает
                  // ширины шапки по содержимому, и колонки разъезжаются
                  style={{ flex: `0 0 ${String(header.getSize())}px` }}
                  className={cn(
                    "relative flex h-[var(--pt-row-h)] min-w-0 items-center",
                    "border-b border-[var(--pt-border-default)]",
                    "px-[var(--pt-pad-x)] text-left font-medium",
                    "text-[var(--pt-fs-xs)] text-[var(--pt-text-secondary)]",
                  )}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanResize() && (
                    <span
                      role="separator"
                      aria-orientation="vertical"
                      aria-label={`Ширина колонки ${header.column.id}`}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      className={cn(
                        "absolute right-0 top-0 h-full w-[6px] cursor-col-resize select-none",
                        "hover:bg-[var(--pt-border-strong)]",
                      )}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>

        <tbody style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {virtualizer.getVirtualItems().map((virtualRow) => {
            const row = rows[virtualRow.index];
            if (!row) {
              return null;
            }
            const accent = rowAccent?.(row);
            return (
              <tr
                key={row.id}
                onClick={() => onRowClick?.(row)}
                aria-selected={row.id === selectedRowId}
                className={cn(
                  "absolute left-0 flex w-full items-center border-b border-[var(--pt-border-subtle)]",
                  "hover:bg-[var(--pt-bg-hover)]",
                  virtualRow.index % 2 === 1 && "bg-[var(--pt-bg-stripe)]",
                  row.id === selectedRowId && "bg-[var(--pt-bg-selected)]",
                  onRowClick && "cursor-pointer",
                )}
                style={{
                  height: virtualRow.size,
                  transform: `translateY(${virtualRow.start}px)`,
                  ...(accent === undefined
                    ? {}
                    : { boxShadow: `inset var(--pt-row-accent-w) 0 0 ${accent}` }),
                }}
              >
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    style={{ flex: `0 0 ${String(cell.column.getSize())}px` }}
                    className={cn(
                      "min-w-0 truncate px-[var(--pt-pad-x)]",
                      "text-[var(--pt-text-primary)]",
                    )}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
